import * as React from "react";
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  Search,
  ChevronDown,
  Check,
  X,
  Loader2,
  Building2,
  Filter,
  RefreshCw,
  ArrowUpDown,
} from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import {
  useSearchPartners,
  PartnerSortOption,
} from "@/hooks/useLocalStorage";
import {
  PartnerSearchItem,
  parsePartnerSearchResults,
} from "@/lib/api-services";
import { cn } from "@/lib/utils";

export const SORT_OPTIONS: {
  label: string;
  value: PartnerSortOption;
  description: string;
}[] = [
  { label: "Standard", value: "Standard", description: "Default order" },
  { label: "Alphabetical", value: "Lex", description: "A to Z" },
  { label: "By IDs", value: "Id_based", description: "Ordered by Partner ID" },
];

export interface PartnerSearchDropdownProps {
  value?: string;
  onChange: (partnerName: string, partnerItem?: PartnerSearchItem) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  defaultSort?: PartnerSortOption;
}

export function PartnerSearchDropdown({
  value = "",
  onChange,
  placeholder = "Partner Name",
  className,
  disabled = false,
  defaultSort = "Lex",
}: PartnerSearchDropdownProps) {
  const [open, setOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [sortBy, setSortBy] = useState<PartnerSortOption>(defaultSort);

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Debounce search query by 300ms
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(searchInput.trim());
    }, 300);
    return () => clearTimeout(handler);
  }, [searchInput]);

  // Infinite query for partner search (with dynamic sort & scroll pagination)
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    refetch,
  } = useSearchPartners(debouncedQuery, sortBy, open);

  // Auto-focus input when opened
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    } else {
      setSearchInput("");
      setDebouncedQuery("");
    }
  }, [open]);

  // Flatten and deduplicate partner search items
  const allPartners = useMemo(() => {
    if (!data?.pages) return [];
    const seen = new Set<string | number>();
    const list: PartnerSearchItem[] = [];

    for (const page of data.pages) {
      const { items } = parsePartnerSearchResults(page.data);
      for (const item of items) {
        if (!seen.has(item.id)) {
          seen.add(item.id);
          list.push(item);
        }
      }
    }
    return list;
  }, [data]);

  // Handle scroll pagination near the bottom
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      if (scrollHeight - scrollTop <= clientHeight + 60) {
        if (hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage]
  );

  // IntersectionObserver for bottom sentinel
  useEffect(() => {
    if (!open || !hasNextPage || isFetchingNextPage) return;

    const sentinel = sentinelRef.current;
    const container = listRef.current;
    if (!sentinel || !container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      {
        root: container,
        rootMargin: "80px",
        threshold: 0.1,
      }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [open, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleSelectPartner = (item: PartnerSearchItem) => {
    onChange(item.partnerName, item);
    setOpen(false);
  };

  const handleSelectAll = () => {
    onChange("");
    setOpen(false);
  };

  const handleSelectCustomQuery = () => {
    if (debouncedQuery) {
      onChange(debouncedQuery);
      setOpen(false);
    }
  };

  const handleClearSelection = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange("");
  };

  const isExactMatchPresent = useMemo(() => {
    if (!debouncedQuery) return false;
    const q = debouncedQuery.toLowerCase();
    return allPartners.some((p) => p.partnerName.toLowerCase() === q);
  }, [debouncedQuery, allPartners]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            "flex h-10 w-full items-center justify-between rounded-md border-black-800 border-2 bg-background px-3 py-2 text-sm ring-offset-background",
            "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
            "disabled:cursor-not-allowed disabled:opacity-50 transition-colors text-left",
            className
          )}
        >
          <div className="flex items-center min-w-0 flex-1 mr-2">
            {value ? (
              <span className="truncate font-medium text-foreground flex items-center">
                <Building2 className="h-3.5 w-3.5 mr-1.5 text-workfinity-600 shrink-0" />
                <span className="truncate">{value}</span>
              </span>
            ) : (
              <span className="text-muted-foreground truncate">{placeholder}</span>
            )}
          </div>

          <div className="flex items-center space-x-1 shrink-0">
            {value && !disabled && (
              <span
                role="button"
                tabIndex={0}
                onClick={handleClearSelection}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    handleClearSelection(e as any);
                  }
                }}
                className="rounded-full p-0.5 hover:bg-muted text-muted-foreground hover:text-foreground cursor-pointer transition-colors"
                title="Clear selection"
              >
                <X className="h-3.5 w-3.5" />
              </span>
            )}
            <ChevronDown
              className={cn(
                "h-4 w-4 opacity-60 transition-transform duration-200",
                open && "transform rotate-180"
              )}
            />
          </div>
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        sideOffset={4}
        className="w-[var(--radix-popover-trigger-width)] min-w-[280px] max-w-[420px] p-0 shadow-lg border border-border bg-popover rounded-md overflow-hidden z-50"
      >
        {/* Search Input Bar */}
        <div className="flex items-center px-3 py-2 border-b border-border bg-muted/30">
          <Search className="h-4 w-4 text-muted-foreground mr-2 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search partner name..."
            className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground text-foreground"
          />
          {searchInput && (
            <button
              type="button"
              onClick={() => setSearchInput("")}
              className="text-muted-foreground hover:text-foreground p-0.5"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
          {isLoading && (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-workfinity-500 ml-1.5 shrink-0" />
          )}
        </div>

        {/* Order By (Sort) Toolbar */}
        <div className="flex items-center justify-between px-3 py-1.5 bg-muted/40 border-b border-border/60 text-xs">
          <span className="flex items-center text-[11px] font-medium text-muted-foreground shrink-0 mr-2">
            <ArrowUpDown className="h-3 w-3 mr-1 text-muted-foreground/70" />
            Order by:
          </span>
          <div className="flex items-center space-x-1 overflow-x-auto py-0.5">
            {SORT_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setSortBy(opt.value)}
                title={opt.description}
                className={cn(
                  "px-2 py-0.5 rounded text-[11px] font-medium transition-all whitespace-nowrap",
                  sortBy === opt.value
                    ? "bg-workfinity-500 text-white shadow-xs font-semibold"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Action Options */}
        <div className="border-b border-border/60 bg-muted/10 p-1">
          {/* Option: All Partners */}
          <button
            type="button"
            onClick={handleSelectAll}
            className={cn(
              "w-full flex items-center justify-between px-2.5 py-1.5 text-xs rounded-sm transition-colors text-left font-medium",
              !value
                ? "bg-workfinity-50 text-workfinity-700 font-semibold"
                : "text-muted-foreground hover:bg-muted hover:text-foreground"
            )}
          >
            <span>All Partners</span>
            {!value && <Check className="h-3.5 w-3.5 text-workfinity-600" />}
          </button>

          {/* Option: Filter by custom substring if typed and not exact match */}
          {debouncedQuery && !isExactMatchPresent && (
            <button
              type="button"
              onClick={handleSelectCustomQuery}
              className="w-full flex items-center px-2.5 py-1.5 text-xs rounded-sm transition-colors text-left text-workfinity-700 hover:bg-workfinity-50 font-medium"
            >
              <Filter className="h-3 w-3 mr-1.5 shrink-0" />
              <span className="truncate">
                Filter as &ldquo;<span className="underline">{debouncedQuery}</span>&rdquo;
              </span>
            </button>
          )}
        </div>

        {/* Scrollable Partners List */}
        <div
          ref={listRef}
          onScroll={handleScroll}
          className="max-h-[260px] overflow-y-auto p-1 divide-y divide-border/20 text-sm"
        >
          {/* Loading Initial State */}
          {isLoading && allPartners.length === 0 && (
            <div className="py-6 text-center text-muted-foreground space-y-2">
              <Loader2 className="h-5 w-5 animate-spin mx-auto text-workfinity-500" />
              <p className="text-xs">Searching partners...</p>
            </div>
          )}

          {/* Error State */}
          {isError && allPartners.length === 0 && (
            <div className="py-6 text-center text-red-500 space-y-2">
              <p className="text-xs">Failed to load partners.</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetch()}
                className="h-7 text-xs"
              >
                <RefreshCw className="h-3 w-3 mr-1" /> Retry
              </Button>
            </div>
          )}

          {/* Empty State */}
          {!isLoading && !isError && allPartners.length === 0 && (
            <div className="py-6 text-center text-muted-foreground">
              <p className="text-xs">No partners found matching &ldquo;{debouncedQuery}&rdquo;</p>
            </div>
          )}

          {/* Partner Items */}
          {allPartners.map((item) => {
            const isSelected = value.toLowerCase() === item.partnerName.toLowerCase();
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => handleSelectPartner(item)}
                className={cn(
                  "w-full flex items-center justify-between px-2.5 py-2 rounded-sm text-left transition-colors group",
                  isSelected
                    ? "bg-workfinity-50 text-workfinity-800 font-medium"
                    : "hover:bg-muted text-foreground"
                )}
              >
                <div className="flex flex-col min-w-0 mr-2">
                  <div className="flex items-center space-x-1.5">
                    <span className="truncate font-medium">{item.partnerName}</span>
                    {item.partnerType && (
                      <span className="text-[10px] px-1.5 py-0.2 rounded bg-muted text-muted-foreground shrink-0 border border-border/40">
                        {item.partnerType}
                      </span>
                    )}
                  </div>
                  {item.description && (
                    <span className="text-[11px] text-muted-foreground truncate max-w-[280px]">
                      {item.description}
                    </span>
                  )}
                </div>

                {isSelected && (
                  <Check className="h-4 w-4 text-workfinity-600 shrink-0 ml-1" />
                )}
              </button>
            );
          })}

          {/* Bottom Pagination Indicator */}
          {isFetchingNextPage && (
            <div className="py-2.5 flex items-center justify-center space-x-2 text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-workfinity-500" />
              <span className="text-xs">Loading more partners...</span>
            </div>
          )}

          {/* All items loaded indicator */}
          {!hasNextPage && allPartners.length >= 20 && !isLoading && (
            <div className="py-2 text-center text-[11px] text-muted-foreground/60 border-t border-border/30 mt-1">
              Showing all {allPartners.length} partners
            </div>
          )}

          {/* Sentinel for IntersectionObserver */}
          <div ref={sentinelRef} className="h-1 w-full pointer-events-none" />
        </div>
      </PopoverContent>
    </Popover>
  );
}
